import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { useApi, useApiQuery } from "@/api/useApi";
import type { ExpensePolicy } from "@/api/types";
import { shortDate } from "@/format";
import { ArchiveCard, DepartmentCodesCard } from "./OfficeSettings";
import { useDialogs } from "@/components/dialogs";
import { DeleteReportCard } from "./DeleteReportCard";
import { DirectoryRefreshCard } from "./DirectoryRefreshCard";
import { RemindersCard } from "./RemindersCard";
import { devPreview } from "@/features";
import type { RegisterPart, SaveAll, SettingsPart } from "./settingsSave";

const FormId = "settings-form";

const Models = [
  { id: "claude-sonnet-5-5", label: "Claude Sonnet 5.5 (balanced, recommended)" },
  { id: "claude-opus-5-5", label: "Claude Opus 5.5 (most careful, slower)" },
  { id: "claude-haiku-5-5", label: "Claude Haiku 5.5 (fastest, cheapest)" },
];

function list(value: string): string[] {
  return value
    .split(/[,;\n]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/** Everything about the expense app that an administrator can change without a deploy. */
export function SettingsPage({
  forwardTo,
  onSaved,
}: {
  forwardTo?: string | null | undefined;
  /** After a save: self-approvers change who someone's reports go to. */
  onSaved?: () => void;
}) {
  const { data, error, loading, reload } = useApiQuery<ExpensePolicy>("/api/expenses/policy");
  const api = useApi();
  const [edits, setEdits] = useState<Partial<Record<string, string>>>({});
  const [result, setResult] = useState<{ ok: boolean; text: string }>();
  const [busy, setBusy] = useState(false);
  const parts = useRef(new Map<string, SettingsPart>());

  if (loading && !data) {
    return (
      <div className="card" aria-busy="true">
        <div className="skeleton line" />
        <div className="skeleton line wide" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="error" role="alert">
        {error?.message ?? "Could not load the settings."}
      </div>
    );
  }

  const value = (key: string, saved: string) => edits[key] ?? saved;
  const set = (key: string) => (next: string) =>
    setEdits((current) => ({ ...current, [key]: next }));
  const rate = value("rate", String(data.mileageRateUsd));
  const accounting = value("accounting", data.accountingMailbox);
  const payroll = value("payroll", data.payrollMailbox);
  const model = value("model", data.claudeModel);
  const domains = value("domains", data.inboxSenderDomains.join(", "));
  const selfApprovers = value("selfApprovers", data.selfApproverUserNames.join(", "));
  const noReceipt = value("noReceipt", (data.noReceiptUserNames ?? []).join(", "));
  const deductions = value("deductions", (data.paycheckDeductionUserNames ?? []).join(", "));
  const everyDays = value("everyDays", String(data.installPromptEveryDays ?? 3));
  const remindDays = value("remindDays", String(data.reminderEveryDays ?? 3));
  const executives = value("executives", (data.executiveUserNames ?? []).join(", "));
  const typedMiles = value("typedMiles", String(data.mileageTypedMilesAllowed ?? true));
  const tripNotes = value("tripNotes", String(data.mileageNotesRequired ?? false));
  const oldDays = value("oldDays", String(data.oldReceiptDays ?? 90));
  const oldMode = value("oldMode", data.oldReceiptMode ?? "Warn");

  const register: RegisterPart = (name, part) => {
    if (part) parts.current.set(name, part);
    else parts.current.delete(name);
  };

  const saveAll: SaveAll = async () => {
    const cards = [...parts.current.values()];
    // One at a time, since a card may stop to ask something first.
    const fields: Record<string, unknown> = {};
    for (const part of cards) Object.assign(fields, await part.fields?.());
    await api.put("/api/expenses/policy", {
      mileageRateUsd: Number(rate),
      accountingMailbox: accounting,
      payrollMailbox: payroll,
      claudeModel: model,
      inboxSenderDomains: list(domains),
      selfApproverUserNames: list(selfApprovers),
      noReceiptUserNames: list(noReceipt),
      paycheckDeductionUserNames: list(deductions),
      installPromptEveryDays: Number(everyDays),
      reminderEveryDays: Number(remindDays),
      executiveUserNames: list(executives),
      mileageTypedMilesAllowed: typedMiles === "true",
      mileageNotesRequired: tripNotes === "true",
      oldReceiptDays: Number(oldDays),
      oldReceiptMode: oldMode,
      ...fields,
    });
    setEdits({});
    try {
      for (const part of cards) await part.after?.();
    } finally {
      reload();
      onSaved?.();
    }
  };

  const saveButton = (
    <button className="btn" form={FormId} disabled={busy}>
      {busy ? "Saving…" : "Save settings"}
    </button>
  );
  const saved = result && (
    <div className={result.ok ? "success" : "error"} role="status">
      {result.text}
    </div>
  );

  return (
    <div className="narrow">
      <div className="page-head">
        <h1>Settings</h1>
        {saveButton}
      </div>
      {saved}
      <p className="muted lead">
        These apply to everyone. A new mileage rate applies to trips added after you save.
      </p>
      <Link className="card link-card" to="/settings/categories">
        <span>
          <strong>Categories & rules</strong>
          <span className="muted">
            Expense types, their limits and receipt rules, brands, and currencies
          </span>
        </span>
        <Icon name="chevronRight" size={18} />
      </Link>
      <Link className="card link-card" to="/settings/delegates">
        <span>
          <strong>Delegates</strong>
          <span className="muted">
            People who do someone else&apos;s expenses, and approve for them
          </span>
        </span>
        <Icon name="chevronRight" size={18} />
      </Link>
      {devPreview && (
        <Link className="card link-card" to="/settings/instructions">
          <span>
            <strong>Instructions</strong>
            <span className="muted">
              The wording of the (i) tips on the add buttons and forwarding
            </span>
          </span>
          <Icon name="chevronRight" size={18} />
        </Link>
      )}
      <form
        id={FormId}
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setResult(undefined);
          saveAll()
            .then(() => setResult({ ok: true, text: "Saved." }))
            .catch((caught: unknown) =>
              setResult({
                ok: false,
                text: caught instanceof Error ? caught.message : String(caught),
              }),
            )
            .finally(() => setBusy(false));
        }}
      >
        <section className="card stack">
          <h2>Mileage</h2>
          <label>
            Mileage rate, per mile
            <span className="input-prefix">
              <span aria-hidden="true">$</span>
              <input
                value={rate}
                onChange={(e) => set("rate")(e.target.value)}
                inputMode="decimal"
              />
            </span>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={typedMiles === "true"}
              onChange={(e) => set("typedMiles")(String(e.target.checked))}
            />
            Let people type the miles for a trip. Off, the miles always come from the start and end
            locations and can&apos;t be changed.
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={tripNotes === "true"}
              onChange={(e) => set("tripNotes")(String(e.target.checked))}
            />
            Require a note on every trip saying what it was for. A trip without one has to be given
            a note before the report can be sent.
          </label>
        </section>

        <section className="card stack">
          <h2>Old receipts and trips</h2>
          <div className="split">
            <label>
              Older than
              <span className="input-suffix">
                <input
                  value={oldDays}
                  onChange={(e) => set("oldDays")(e.target.value)}
                  inputMode="numeric"
                  pattern="[0-9]+"
                />
                <span aria-hidden="true">days</span>
              </span>
            </label>
            <label>
              When one is
              <select value={oldMode} onChange={(e) => set("oldMode")(e.target.value)}>
                <option value="Warn">Allow, with a warning</option>
                <option value="Block">Reject it</option>
                <option value="Off">Don't check</option>
              </select>
            </label>
          </div>
          <p className="muted">
            Applies to receipts and mileage trips. Age is counted from the receipt or trip date to
            the day the report is sent. A warning shows on the expense for the employee, their
            manager, payroll, and any executive asked to approve it. A rejected one has to be
            removed before the report can be sent.
          </p>
        </section>

        <section className="card stack">
          <h2>Approval mail</h2>
          <label>
            Payroll mailbox
            <input value={payroll} onChange={(e) => set("payroll")(e.target.value)} type="email" />
          </label>
          <p className="muted">
            Told when a manager approves a report, and when an executive approves or declines one.
          </p>
        </section>

        <RemindersCard
          policy={data}
          everyDays={remindDays}
          onEveryDays={set("remindDays")}
          onSent={reload}
        />

        <section className="card stack">
          <h2>Forwarded receipts</h2>
          {forwardTo && (
            <dl className="facts">
              <div>
                <dt>People forward receipts to</dt>
                <dd>{forwardTo}</dd>
              </div>
            </dl>
          )}
          <label>
            Accepted sender domains
            <input
              value={domains}
              onChange={(e) => set("domains")(e.target.value)}
              placeholder="planetart.com"
              autoComplete="off"
            />
          </label>
          <p className="muted">
            Separate with commas. Mail from any other domain is ignored, since a From address is
            easy to forge.
          </p>
          <label>
            Claude model
            <select value={model} onChange={(e) => set("model")(e.target.value)}>
              {Models.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <p className="muted">Reads receipt photos and forwarded emails.</p>
        </section>

        <section className="card stack">
          <h2>Install prompt</h2>
          <p className="muted">
            Asks people to install PA Expenses on their phone or tablet until they do: the first
            time they use it on a device, then again every few days.
          </p>
          <label>
            Ask again every
            <span className="input-suffix">
              <input
                value={everyDays}
                onChange={(e) => set("everyDays")(e.target.value)}
                inputMode="numeric"
                pattern="[0-9]+"
              />
              <span aria-hidden="true">days</span>
            </span>
          </label>
          <p className="muted">Set to 0 to ask only the first time.</p>
        </section>

        <section className="card stack">
          <h2>Executive approvers</h2>
          <label>
            Executives payroll can ask
            <input
              value={executives}
              onChange={(e) => set("executives")(e.target.value)}
              placeholder="name@planetart.com"
              autoComplete="off"
            />
          </label>
          <p className="muted">
            Sign-in addresses, separated by commas. Payroll can send a report to one or more of them
            for approval before paying it, and any one of them can approve. They also need to be in
            the Expenses user group to open it.
          </p>
        </section>

        <section className="card stack">
          <h2>Self Approvers</h2>
          <label>
            Approve their own reports
            <input
              value={selfApprovers}
              onChange={(e) => set("selfApprovers")(e.target.value)}
              placeholder="name@planetart.com"
              autoComplete="off"
            />
          </label>
          <p className="muted">
            Reports these people submit come back to them to approve, then go on to payroll. It can
            also be used for testing, so one person can try every step.
          </p>
        </section>

        <section className="card stack">
          <h2>Executive exceptions</h2>
          <label>
            No receipt needed
            <input
              value={noReceipt}
              onChange={(e) => set("noReceipt")(e.target.value)}
              placeholder="name@planetart.com"
              autoComplete="off"
            />
          </label>
          <p className="muted">
            These people&apos;s expenses never need a receipt. An email saying what they spent, such
            as &ldquo;I had a $200 dinner with the board&rdquo;, is enough.
          </p>
          <label>
            Paycheck deductions
            <input
              value={deductions}
              onChange={(e) => set("deductions")(e.target.value)}
              placeholder="name@planetart.com"
              autoComplete="off"
            />
          </label>
          <p className="muted">
            These people, and their delegates, get a Deduct from Paycheck box under Personal
            Expense, and their reports can add up to less than zero. What they owe goes to payroll
            as a deduction.
          </p>
        </section>

        <ArchiveCard policy={data} register={register} saveAll={saveAll} />
        <DepartmentCodesCard policy={data} register={register} saveAll={saveAll} onSaved={reload} />
        <RetentionCard policy={data} register={register} />
        <ClaudeKeyCard register={register} />
      </form>
      <DeleteReportCard />
      <DirectoryRefreshCard policy={data} onRefreshed={reload} />
      {saved}
      {saveButton}
    </div>
  );
}

/**
 * How long reports are kept. Changing it deletes records for good once they pass the new period,
 * so it's asked twice: once to confirm, then by typing the new number.
 */
function RetentionCard({ policy, register }: { policy: ExpensePolicy; register: RegisterPart }) {
  const current = policy.retentionYears ?? 7;
  const [years, setYears] = useState(String(current));
  const next = Number(years);
  const dialogs = useDialogs();

  useEffect(() => {
    register("retention", {
      fields: async () => {
        if (!Number.isInteger(next) || next < 1 || next > 30)
          throw new Error("Keep reports for between 1 and 30 years.");
        if (next === current) return {};
        const yes = await dialogs.confirm({
          title: `Keep reports for ${String(next)} years?`,
          message:
            `Instead of ${String(current)}. Reports older than that are permanently deleted from the app, with their receipts.\n\n` +
            "Archived zips are kept.",
          confirmLabel: "Change retention",
          danger: true,
          typeToConfirm: String(next),
        });
        if (!yes) throw new Error("Not saved: the retention change was cancelled.");
        return { retentionYears: next, retentionConfirmation: next };
      },
    });
    return () => register("retention", undefined);
  });

  return (
    <section className="card stack">
      <h2>Retention</h2>
      <p className="muted">
        Reports are kept for {current} years after they're paid (or last changed, if never paid),
        then deleted from the app with their receipts. The archived zips are kept.
      </p>
      <label>
        Keep reports for
        <span className="input-suffix">
          <input
            value={years}
            onChange={(e) => setYears(e.target.value)}
            inputMode="numeric"
            pattern="[0-9]+"
          />
          <span aria-hidden="true">years</span>
        </span>
      </label>
      {next !== current && (
        <p className="muted">You&apos;ll be asked to confirm the change when you save.</p>
      )}
    </section>
  );
}

interface ClaudeKeyStatus {
  configured: boolean;
  hint: string | null;
  updatedAt: string | null;
  editable: boolean;
}

/** The Claude key that reads receipts. Shown masked; it can be replaced or removed, never read. */
function ClaudeKeyCard({ register }: { register: RegisterPart }) {
  const { data, error, loading } = useApiQuery<ClaudeKeyStatus>("/api/expenses/settings/ai");
  const api = useApi();
  const dialogs = useDialogs();
  const [status, setStatus] = useState<ClaudeKeyStatus>();
  const [key, setKey] = useState("");
  const [result, setResult] = useState<{ ok: boolean; text: string }>();
  const [busy, setBusy] = useState(false);
  const current = status ?? data;

  function save(work: Promise<ClaudeKeyStatus>, done: string) {
    setBusy(true);
    setResult(undefined);
    work
      .then((updated) => {
        setStatus(updated);
        setKey("");
        setResult({ ok: true, text: done });
      })
      .catch((caught: unknown) =>
        setResult({ ok: false, text: caught instanceof Error ? caught.message : String(caught) }),
      )
      .finally(() => setBusy(false));
  }

  useEffect(() => {
    register("claudeKey", {
      after: async () => {
        if (key.trim() === "") return;
        const updated = await api.put<ClaudeKeyStatus>("/api/expenses/settings/ai", {
          apiKey: key,
        });
        setStatus(updated);
        setKey("");
        setResult({ ok: true, text: "Key saved. Receipts are read with it within a minute." });
      },
    });
    return () => register("claudeKey", undefined);
  });

  return (
    <section className="card stack">
      <h2>Receipt reading</h2>
      <p className="muted">
        Claude reads receipt photos and forwarded emails to fill in the merchant, total, date and
        category.
      </p>
      {loading && !current && <div className="skeleton line" />}
      {error && !current && (
        <div className="error" role="alert">
          {error.message}
        </div>
      )}
      {current && (
        <dl className="facts">
          <div>
            <dt>Claude API key</dt>
            <dd>
              {current.configured
                ? `${current.hint ?? ""}${current.updatedAt ? ` · set ${shortDate(current.updatedAt.slice(0, 10))}` : ""}`
                : "Not set — receipts aren’t read automatically"}
            </dd>
          </div>
        </dl>
      )}
      {current?.editable === false ? (
        <p className="muted">This environment takes the key from its configuration.</p>
      ) : (
        <>
          <label>
            {current?.configured ? "Replace key" : "Key"}
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="sk-ant-…"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          {result && (
            <div className={result.ok ? "success" : "error"} role="status">
              {result.text}
            </div>
          )}
          {current?.configured && (
            <button
              type="button"
              className="btn ghost danger-text"
              disabled={busy}
              onClick={() => {
                void (async () => {
                  const yes = await dialogs.confirm({
                    title: "Remove the Claude key?",
                    message: "Receipts stop being read automatically.",
                    confirmLabel: "Remove key",
                    danger: true,
                  });
                  if (yes)
                    save(api.del<ClaudeKeyStatus>("/api/expenses/settings/ai"), "Key removed.");
                })();
              }}
            >
              Remove key
            </button>
          )}
        </>
      )}
    </section>
  );
}
